// Practice Projects Management Page - Refactored
// Date: January 21, 2026
// Architecture: Follows CreateProject pattern with decomposed components

import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import {
  PracticeProjectStats,
  PracticeProjectFilters,
} from '../../components/PracticeProjects';
import {
  usePracticeProjectsQuery,
  useDeletePracticeProjectMutation,
  usePublishPracticeProjectMutation,
  useUnpublishPracticeProjectMutation,
} from '../../api/practiceProjectsApi';
import { PracticeProject } from '../../types/practiceProjects';
import paths from '../../routes';
import { usePracticeProjectFilters, usePracticeProjectDialogs } from './hooks';
import {
  ManagementHeader,
  ProjectsTable,
  ProjectsTableHeader,
  TablePagination,
  TableSkeleton,
  EmptyState,
  DeleteProjectDialog,
  PublishProjectDialog,
  UnpublishProjectDialog,
} from './ManagementPage';
import { toast } from 'sonner';

export default function PracticeProjectsManagementPage() {
  const navigate = useNavigate();

  // Custom hooks for state management
  const {
    filters,
    apiFilters,
    handlePageChange,
    handleQuickSearch,
    handleFiltersChange,
  } = usePracticeProjectFilters();

  const {
    deleteDialog,
    publishDialog,
    unpublishDialog,
    openDeleteDialog,
    closeDeleteDialog,
    openPublishDialog,
    closePublishDialog,
    openUnpublishDialog,
    closeUnpublishDialog,
  } = usePracticeProjectDialogs();

  // React Query hooks
  const { data, isLoading, isError } = usePracticeProjectsQuery(apiFilters);
  const deleteMutation = useDeletePracticeProjectMutation();
  const publishMutation = usePublishPracticeProjectMutation();
  const unpublishMutation = useUnpublishPracticeProjectMutation();

  // Data
  const pagination = data?.data?.pagination;
  const projects = data?.data?.projects || [];

  // Handlers
  const handleCreateNew = () => {
    navigate(paths.createPracticeProject);
  };

  const handleEdit = (projectId: string) => {
    navigate(paths.toEditPracticeProject(projectId));
  };

  const handleView = (projectId: string) => {
    navigate(paths.toPracticeProjectDetails(projectId));
  };

  const handleDeleteConfirm = async () => {
    if (deleteDialog.projectId) {
      await deleteMutation.mutateAsync(deleteDialog.projectId);
      closeDeleteDialog();
    }
  };

  const handlePublishConfirm = async () => {
    if (publishDialog.projectId) {
      await publishMutation.mutateAsync(publishDialog.projectId);
      closePublishDialog();
    }
  };

  const handleUnpublishConfirm = async () => {
    if (unpublishDialog.projectId) {
      await unpublishMutation.mutateAsync(unpublishDialog.projectId);
      closeUnpublishDialog();
    }
  };

  const handleDuplicate = (project: PracticeProject) => {
    toast.info('Duplicate functionality coming soon');
  };

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <ManagementHeader onCreateNew={handleCreateNew} />

      {/* Statistics Dashboard */}
      <PracticeProjectStats variant="admin" />

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Filters Sidebar */}
        <div className="lg:col-span-1">
          <PracticeProjectFilters
            filters={filters}
            onFiltersChange={handleFiltersChange}
            showStatusFilter={true}
          />
        </div>

        {/* Projects Table */}
        <div className="lg:col-span-3">
          <Card>
            <ProjectsTableHeader
              searchValue={filters.search}
              onSearchChange={handleQuickSearch}
            />
            <CardContent>
              {isLoading ? (
                <TableSkeleton />
              ) : isError ? (
                <div className="text-center py-8 text-destructive">
                  Failed to load practice projects
                </div>
              ) : projects.length === 0 ? (
                <EmptyState onCreateNew={handleCreateNew} />
              ) : (
                <>
                  <ProjectsTable
                    projects={projects}
                    onView={handleView}
                    onEdit={handleEdit}
                    onPublish={openPublishDialog}
                    onUnpublish={openUnpublishDialog}
                    onDuplicate={handleDuplicate}
                    onDelete={openDeleteDialog}
                  />
                  {pagination && (
                    <TablePagination
                      pagination={pagination}
                      onPageChange={handlePageChange}
                    />
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Dialogs */}
      <DeleteProjectDialog
        open={deleteDialog.open}
        onOpenChange={closeDeleteDialog}
        projectTitle={deleteDialog.projectTitle}
        onConfirm={handleDeleteConfirm}
        isDeleting={deleteMutation.isPending}
      />

      <PublishProjectDialog
        open={publishDialog.open}
        onOpenChange={closePublishDialog}
        projectTitle={publishDialog.projectTitle}
        onConfirm={handlePublishConfirm}
        isPublishing={publishMutation.isPending}
      />

      <UnpublishProjectDialog
        open={unpublishDialog.open}
        onOpenChange={closeUnpublishDialog}
        projectTitle={unpublishDialog.projectTitle}
        onConfirm={handleUnpublishConfirm}
        isUnpublishing={unpublishMutation.isPending}
      />
    </div>
  );
}
